import { useQuery } from '@tanstack/react-query';
import {
  Users,
  BarChart,
  Building,
  DollarSign,
  Settings
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { AcademyOverview } from './components/AcademyOverview';
import { ResourceManagement } from './components/ResourceManagement';
import { TeamManagement } from './components/TeamManagement';
import { OperationalInsights } from './components/OperationalInsights';

interface AcademyStats {
  totalPlayers: number;
  activeCoaches: number;
  totalSessions: number;
  facilitiesCount: number;
  monthlyRevenue: number;
  utilization: {
    coaches: number;
    facilities: number;
    equipment: number;
  };
  performance: {
    averageImprovement: number;
    retention: number;
    satisfaction: number;
  };
  topPerformers: Array<{
    id: string;
    name: string;
    score: number;
    improvement: number;
    coachName: string;
  }>;
  recentActivity: Array<{
    id: string;
    type: 'session' | 'achievement' | 'milestone';
    description: string;
    timestamp: string;
    playerName?: string;
    coachName?: string;
  }>;
  resourceAllocation: {
    coachWorkload: Array<{
      id: string;
      name: string;
      activeStudents: number;
      weeklyHours: number;
      utilization: number;
      rating: number;
    }>;
    facilityUsage: Array<{
      id: string;
      name: string;
      type: string;
      hoursBooked: number;
      maxHours: number;
      utilization: number;
    }>;
    equipmentStatus: Array<{
      id: string;
      type: string;
      available: number;
      total: number;
      maintenanceScheduled: number;
    }>;
  };
}

export function AcademyDashboard() {
  const { organization } = useAuth();

  const { data: stats, isLoading } = useQuery<AcademyStats>({
    queryKey: ['academy-dashboard-stats', organization?.id],
    queryFn: async () => {
      // This would normally fetch from academy analytics endpoints
      // For now, returning comprehensive mock data
      return {
        totalPlayers: 156,
        activeCoaches: 12,
        totalSessions: 2847,
        facilitiesCount: 8,
        monthlyRevenue: 45200,
        utilization: {
          coaches: 78,
          facilities: 65,
          equipment: 82
        },
        performance: {
          averageImprovement: 23.5,
          retention: 89.2,
          satisfaction: 4.6
        },
        topPerformers: [
          { id: '1', name: 'Alex Johnson', score: 9.2, improvement: 12, coachName: 'Sarah Wilson' },
          { id: '2', name: 'Emma Chen', score: 8.9, improvement: 18, coachName: 'Mike Davis' },
          { id: '3', name: 'David Kumar', score: 8.7, improvement: 15, coachName: 'Lisa Brown' },
          { id: '4', name: 'Sofia Martinez', score: 8.5, improvement: 22, coachName: 'James Lee' },
          { id: '5', name: 'Ryan Thompson', score: 8.3, improvement: 9, coachName: 'Sarah Wilson' }
        ],
        recentActivity: [
          {
            id: '1',
            type: 'achievement',
            description: 'Reached 100+ meter milestone',
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
            playerName: 'Alex Johnson',
            coachName: 'Sarah Wilson'
          },
          {
            id: '2',
            type: 'session',
            description: 'Group batting session completed',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            coachName: 'Mike Davis'
          },
          {
            id: '3',
            type: 'milestone',
            description: 'Monthly improvement target achieved',
            timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
            playerName: 'Emma Chen'
          },
          {
            id: '4',
            type: 'session',
            description: 'Technical skills workshop',
            timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
            coachName: 'Lisa Brown'
          }
        ],
        resourceAllocation: {
          coachWorkload: [
            { id: '1', name: 'Sarah Wilson', activeStudents: 18, weeklyHours: 32, utilization: 80, rating: 4.8 },
            { id: '2', name: 'Mike Davis', activeStudents: 15, weeklyHours: 28, utilization: 70, rating: 4.6 },
            { id: '3', name: 'Lisa Brown', activeStudents: 20, weeklyHours: 35, utilization: 88, rating: 4.9 },
            { id: '4', name: 'James Lee', activeStudents: 12, weeklyHours: 24, utilization: 60, rating: 4.5 }
          ],
          facilityUsage: [
            { id: '1', name: 'Main Practice Net', type: 'batting', hoursBooked: 42, maxHours: 60, utilization: 70 },
            { id: '2', name: 'Indoor Training Hall', type: 'multi-purpose', hoursBooked: 38, maxHours: 50, utilization: 76 },
            { id: '3', name: 'Bowling Machine Area', type: 'bowling', hoursBooked: 25, maxHours: 40, utilization: 63 },
            { id: '4', name: 'Fitness Center', type: 'conditioning', hoursBooked: 30, maxHours: 45, utilization: 67 }
          ],
          equipmentStatus: [
            { id: '1', type: 'Bowling Machines', available: 8, total: 10, maintenanceScheduled: 2 },
            { id: '2', type: 'Batting Cages', available: 5, total: 6, maintenanceScheduled: 1 },
            { id: '3', type: 'Analysis Cameras', available: 12, total: 15, maintenanceScheduled: 3 },
            { id: '4', type: 'Protective Gear Sets', available: 45, total: 50, maintenanceScheduled: 5 }
          ]
        }
      } as AcademyStats;
    },
    enabled: !!organization?.id
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="space-y-4">
          <div className="h-8 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3" />
        </div>
        {Array(3).fill(0).map((_, i) => (
          <div key={i} className="grid gap-4 md:grid-cols-4">
            {Array(4).fill(0).map((_, j) => (
              <div key={j} className="h-32 bg-gray-200 rounded-lg animate-pulse" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center">
          <Building className="mr-3 h-8 w-8 text-primary" />
          Academy Management
        </h1>
        <p className="text-muted-foreground mt-2">
          Comprehensive oversight and optimization of {organization?.name} academy operations
        </p>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" className="flex items-center">
            <BarChart className="mr-2 h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="resources" className="flex items-center">
            <Settings className="mr-2 h-4 w-4" />
            Resources
          </TabsTrigger>
          <TabsTrigger value="teams" className="flex items-center">
            <Users className="mr-2 h-4 w-4" />
            Teams
          </TabsTrigger>
          <TabsTrigger value="operations" className="flex items-center">
            <DollarSign className="mr-2 h-4 w-4" />
            Operations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <AcademyOverview stats={stats} />
        </TabsContent>

        <TabsContent value="resources" className="space-y-6">
          <ResourceManagement resourceData={stats?.resourceAllocation} />
        </TabsContent>

        <TabsContent value="teams" className="space-y-6">
          <TeamManagement 
            topPerformers={stats?.topPerformers || []}
            totalPlayers={stats?.totalPlayers || 0}
            activeCoaches={stats?.activeCoaches || 0}
          />
        </TabsContent>

        <TabsContent value="operations" className="space-y-6">
          <OperationalInsights 
            monthlyRevenue={stats?.monthlyRevenue || 0}
            performance={stats?.performance}
            organization={organization}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}